import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { useParams } from 'react-router';
import { assistantMailStatusQuery } from '../api/ai';
import { sentMailDetailQuery, sentMailLink } from '../api/notifications';
import { BackLink, Notice } from '../ops/shared';
import { TwoColumns } from '../shell';
import { AdminFrame } from './frame';
import { buttonClassName, Card, formatTimestamp, Icon, StatusPill } from '../ui';
import { emlMissing, MAIL_STATUS, messageHelp, recipientWords, transportWords } from './presenter';
import { WriteAsDialog } from './WriteAsDialog';
import o from '../ops/Ops.module.css';
import s from '../close/Close.module.css';

/**
 * One email the app sent or tried to send: its text, its HTML in a sandboxed frame, where it went
 * and the .eml copy, each in words true to what became of it (RUI-012).
 */
export function SentMailDetailPage(): ReactElement {
  const id = useParams()['id'] ?? '';
  const { data: mail } = useSuspenseQuery(sentMailDetailQuery(id));
  const to = recipientWords(mail);
  const status = MAIL_STATUS[mail.status];
  const html = sentMailLink(mail.links.html, mail.id, 'html');
  const eml = sentMailLink(mail.links.eml, mail.id, 'eml');
  const missing = emlMissing(mail);
  const help = messageHelp(mail.status);
  // APP-071: in test environments, a named tester can answer any email as someone.
  const assistant = useQuery(assistantMailStatusQuery).data;
  const [replying, setReplying] = useState(false);
  const recipient =
    mail.recipient.kind === 'person' && mail.recipient.personId !== null
      ? { id: mail.recipient.personId, name: mail.recipient.name }
      : null;

  return (
    <>
      <AdminFrame
        hero={{
          eyebrow: `Settings · System administration · Sent mail · ${mail.eventLabel}`,
          title: mail.subject,
          sub: `To ${to.name} · ${formatTimestamp(mail.createdAt)}`,
          titleTestId: 'sent-mail-detail-title',
          ...(assistant?.available === true && {
            actions: [
              {
                label: recipient === null ? 'Reply as someone' : `Reply as ${recipient.name}`,
                icon: 'mail',
                onPress: () => {
                  setReplying(true);
                },
              },
            ],
          }),
        }}
      >
        <div className={o.stack}>
          <BackLink href="/settings/system/sent-mail">Sent mail</BackLink>
          <TwoColumns
            sideLabel="Delivery"
            main={
              <>
                <Card
                  title="The email as written"
                  help={{
                    shows: help.html,
                    canDo: 'Read it. The plain-text version is below.',
                  }}
                >
                  {html === null ? (
                    <Notice tone="info">The HTML version isn’t available for this message.</Notice>
                  ) : (
                    <iframe
                      title={`HTML version of “${mail.subject}”`}
                      src={html}
                      sandbox=""
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      className={s.mailFrame}
                    />
                  )}
                </Card>
                <Card
                  title="Plain text"
                  help={{
                    shows: help.text,
                    canDo: 'Read or copy it.',
                  }}
                >
                  <pre className={s.mailText}>{mail.textBody}</pre>
                </Card>
              </>
            }
            side={
              <>
                <Card
                  title="Delivery"
                  compact
                  help={{
                    shows: 'Who the email was for, where it actually went, and how sending went.',
                    canDo: 'Download the .eml copy when there is one.',
                  }}
                >
                  <StatusPill tone={status.tone} label={status.label} />
                  <dl className={s.factList}>
                    <div>
                      <dt>To</dt>
                      <dd>
                        {to.name}
                        <span className={o.sub}>{to.address}</span>
                      </dd>
                    </div>
                    {(['cc', 'bcc'] as const).map((mode) => {
                      const copied = mail.copiedTo.filter((c) => c.mode === mode);
                      return (
                        <div key={mode}>
                          <dt>{mode === 'cc' ? 'Copied to (CC)' : 'Copied to (BCC)'}</dt>
                          <dd>
                            {copied.length === 0
                              ? 'No one'
                              : copied.map((c) => (
                                  <span key={c.address} className={o.sub}>
                                    {c.name === '' ? c.address : `${c.name} · ${c.address}`}
                                  </span>
                                ))}
                          </dd>
                        </div>
                      );
                    })}
                    <div>
                      <dt>Delivered to</dt>
                      <dd>{mail.deliveredTo ?? 'Not delivered'}</dd>
                    </div>
                    <div>
                      <dt>How</dt>
                      <dd>{transportWords(mail.transport)}</dd>
                    </div>
                    <div>
                      <dt>Attempts</dt>
                      <dd>{mail.attempts}</dd>
                    </div>
                    {mail.lastError !== null && (
                      <div>
                        <dt>Last problem</dt>
                        <dd>{mail.lastError}</dd>
                      </div>
                    )}
                    <div>
                      <dt>Created</dt>
                      <dd>{formatTimestamp(mail.createdAt)}</dd>
                    </div>
                    <div>
                      <dt>Finished</dt>
                      <dd>
                        {mail.finishedAt === null ? 'Not yet' : formatTimestamp(mail.finishedAt)}
                      </dd>
                    </div>
                    {mail.nextAttemptAt !== null && (
                      <div>
                        <dt>Next try</dt>
                        <dd>{formatTimestamp(mail.nextAttemptAt)}</dd>
                      </div>
                    )}
                    <div>
                      <dt>Award</dt>
                      <dd>
                        {mail.award === null ? (
                          'Not about one award'
                        ) : (
                          <Link href={`/awards/${mail.award.id}`} className={o.inlineLink}>
                            {mail.award.displayId ?? 'Open the award'}
                          </Link>
                        )}
                      </dd>
                    </div>
                  </dl>
                  {eml !== null ? (
                    <a
                      href={eml}
                      download
                      className={buttonClassName({ variant: 'ghost', size: 'sm' })}
                    >
                      <Icon name="download" size={16} />
                      Download .eml
                    </a>
                  ) : (
                    missing !== null && <p className={o.muted}>{missing}</p>
                  )}
                </Card>
              </>
            }
          />
        </div>
      </AdminFrame>
      {assistant?.available === true && (
        // Keyed by the email, so moving to the assistant's answer starts a fresh reply.
        <WriteAsDialog
          key={mail.id}
          isOpen={replying}
          onOpenChange={setReplying}
          replyTo={{ id: mail.id, subject: mail.subject }}
          initialPerson={recipient}
          address={assistant.address}
        />
      )}
    </>
  );
}
